import { useEffect } from "react";
import { useI18n } from "../i18n";

/**
 * Per-page SEO component using direct DOM manipulation.
 *
 * Rationale: react-helmet-async has known append-vs-replace inconsistencies with
 * React 19 concurrent rendering. Direct DOM ops are deterministic and small enough
 * that we control every update precisely.
 *
 * On mount: overrides title, description, canonical, og/twitter, hreflang.
 * On unmount: restores base values so no state bleeds between routes.
 */
export default function SEO({
  title,
  description,
  path = "/",
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const { lang } = useI18n();

  useEffect(() => {
    const base = "https://gianoholding.com";
    const url = `${base}${path}`;
    const t = lang === "it";
    const fullTitle = title
      ? `${title} — GIANO`
      : t
        ? "GIANO — Holding permanente per le PMI italiane"
        : "GIANO — Permanent holding for Italian SMEs";
    const desc = description
      || (t
        ? "GIANO acquisisce PMI italiane senza successione e le mantiene nel gruppo. Compriamo aziende italiane. Per sempre."
        : "GIANO acquires Italian SMEs without succession and keeps them in the group. We buy Italian companies. Forever.");
    const ogImage = image || `${base}/og-image.jpg`;

    // Save originals to restore on unmount
    const originalTitle = document.title;
    const restoreTasks = [];

    document.title = fullTitle;
    restoreTasks.push(() => { document.title = originalTitle; });

    // Helper: upsert a <meta> or <link> by unique selector
    const upsert = (selector, tagName, attrs, isDynamic = true) => {
      let el = document.head.querySelector(selector);
      if (el) {
        // Save original attributes for restore
        const originalAttrs = {};
        for (const k of Object.keys(attrs)) originalAttrs[k] = el.getAttribute(k);
        for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
        restoreTasks.push(() => {
          for (const [k, v] of Object.entries(originalAttrs)) {
            if (v === null) el.removeAttribute(k);
            else el.setAttribute(k, v);
          }
        });
      } else if (isDynamic) {
        el = document.createElement(tagName);
        for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
        el.dataset.seo = "dynamic";
        document.head.appendChild(el);
        restoreTasks.push(() => { el.remove(); });
      }
    };

    upsert('meta[name="description"]', 'meta', { name: 'description', content: desc });
    upsert('link[rel="canonical"]', 'link', { rel: 'canonical', href: url });
    upsert('meta[property="og:title"]', 'meta', { property: 'og:title', content: fullTitle });
    upsert('meta[property="og:description"]', 'meta', { property: 'og:description', content: desc });
    upsert('meta[property="og:url"]', 'meta', { property: 'og:url', content: url });
    upsert('meta[property="og:type"]', 'meta', { property: 'og:type', content: type });
    upsert('meta[property="og:image"]', 'meta', { property: 'og:image', content: ogImage });
    upsert('meta[property="og:locale"]', 'meta', { property: 'og:locale', content: lang === "it" ? "it_IT" : "en_US" });
    upsert('meta[name="twitter:title"]', 'meta', { name: 'twitter:title', content: fullTitle });
    upsert('meta[name="twitter:description"]', 'meta', { name: 'twitter:description', content: desc });
    upsert('meta[name="twitter:image"]', 'meta', { name: 'twitter:image', content: ogImage });

    // html lang
    document.documentElement.setAttribute("lang", lang === "it" ? "it" : "en");

    // Remove existing hreflang links and add fresh
    const existing = document.head.querySelectorAll('link[rel="alternate"][hreflang]');
    existing.forEach(l => l.remove());
    const hreflangs = [
      { hreflang: "it", href: url },
      { hreflang: "en", href: `${url}?lang=en` },
      { hreflang: "x-default", href: url },
    ];
    const created = hreflangs.map(({ hreflang, href }) => {
      const l = document.createElement("link");
      l.setAttribute("rel", "alternate");
      l.setAttribute("hreflang", hreflang);
      l.setAttribute("href", href);
      l.dataset.seo = "dynamic-hreflang";
      document.head.appendChild(l);
      return l;
    });
    restoreTasks.push(() => {
      created.forEach(l => l.remove());
      // Note: original hreflangs from index.html will be restored on next SEO instance
      // or remain removed — for SPA this is acceptable since every route re-adds them.
    });

    // noindex robots meta
    if (noindex) {
      upsert('meta[name="robots"]', 'meta', { name: 'robots', content: 'noindex, nofollow' });
    }

    // Page-specific JSON-LD
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seo = "dynamic-jsonld";
      s.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(s);
      restoreTasks.push(() => { s.remove(); });
    }

    return () => {
      restoreTasks.forEach(fn => { try { fn(); } catch (e) {} });
    };
  }, [title, description, path, image, type, jsonLd, noindex, lang]);

  return null;
}
